import type { Dayjs } from 'dayjs'

import CircularProgress from '@mui/material/CircularProgress'
import clsx from 'clsx'
import { type MutableRefObject, useEffect, useRef } from 'react'
import { useAppSelector, useIntersectionObserver } from 'shared/hooks'

import { FullLoader } from '../../../../components/atoms/FullLoader/FullLoader.tsx'
import { api } from '../../api.ts'
import { useCurrentId } from '../../hooks/index.ts'
import { selectors } from '../../utils/selectors.ts'
import { formatChatDate } from '../../utils/utils.ts'
import { PULLING_OPTIONS } from '../const.ts'
import { useIsPulling } from '../FilterForm/TempDebug.tsx'
import { Message } from '../Message/Message.tsx'
import css from './Chat.module.scss'
// import { restoreScroll } from './utils.ts'

const getId = (id: string, day?: Dayjs) => `${id}-${day?.unix()}`

export function Conversation({ className }: { className?: string }) {
	const scrollableRef = useRef<HTMLDivElement | null>(null)
	const conversation = useCurrentId()

	const { days, map, prevCursor } = useAppSelector(root =>
		selectors.conversation(root, conversation)
	)

	const [chatSpinner, entry] = useIntersectionObserver({
		root: scrollableRef.current
	})

	const isPulling = useIsPulling()

	const recentQuery = api.useMessagesQuery(
		conversation,
		isPulling
			? {
					...PULLING_OPTIONS,
					skip: !conversation
				}
			: {
					skip: !conversation
				}
	)

	api.useMessagesPrevQuery(
		{ id: conversation, prevCursor },
		{
			skip: !(conversation && prevCursor && entry?.isIntersecting)
		}
	)

	const scrollId = useRef('')

	const showLoader =
		recentQuery.isLoading ||
		(recentQuery.isFetching && !recentQuery.currentData)

	// biome-ignore lint/correctness/useExhaustiveDependencies: No need
	useEffect(() => {
		if (prevCursor && entry?.isIntersecting && !scrollId.current) {
			scrollId.current = getId(conversation, days[0])

			return () => {
				setTimeout(() => {
					document.getElementById(scrollId.current)?.scrollIntoView({
						behavior: 'instant',
						block: 'start',
						inline: 'nearest'
					})

					scrollId.current = ''
				}, 0)
			}
		}
	}, [prevCursor, entry?.isIntersecting])

	useScrollToEnd(scrollableRef, !showLoader)

	return (
		<div
			className={clsx(css.chat, 'app-scroll', className)}
			id='chat'
			ref={scrollableRef}
		>
			{showLoader && <FullLoader size={40} />}
			{prevCursor && (
				<div className={css.prev} ref={chatSpinner}>
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						color='primary'
						size={20}
					/>
				</div>
			)}

			{days.map(day => {
				const key = day.toISOString()

				return (
					<section
						className={css.section}
						id={getId(conversation, day)}
						key={key}
					>
						<time className={css.date} dateTime={key}>
							{formatChatDate(day)}
						</time>
						<div className={css.messages}>
							{map[key].map(message => (
								<Message
									id={message.id}
									key={message.id}
									showAvatar={message.showAvatar}
								/>
							))}
						</div>
					</section>
				)
			})}
		</div>
	)
}

function useScrollToEnd(
	scrollableRef: MutableRefObject<HTMLDivElement | null>,
	isReady = false
) {
	const firstRender = useRef(false)
	useEffect(() => {
		if (isReady && !firstRender.current) {
			const wrapper = scrollableRef.current

			if (wrapper) {
				const children = wrapper.childNodes
				const last = children[children.length - 1]

				if (last instanceof HTMLElement) {
					last?.scrollIntoView({
						behavior: 'auto',
						block: 'end',
						inline: 'nearest'
					})

					firstRender.current = true
				}
			}
		}
	}, [isReady, scrollableRef])
}
